/* ──────────────────────────────────────────────────────────────────────
   Global enterprise date-formatting helpers.

   Single source of truth for every user-facing date / datetime in the
   CRM. Raw ISO strings ("2026-05-17T07:32:18.000000Z") must NEVER
   reach the DOM — render through one of the helpers below.

   Conventions:
     • Locale is hard-coded to "en-US" so the output is consistent
       regardless of the operator's browser locale (the alternative
       was system-default, which would render the same value as
       "May 17, 2026", "17/05/2026", or "2026/5/17" depending on
       who's logged in).
     • Every helper accepts `string | Date | number | null | undefined`
       and returns "—" on falsy / invalid input so the caller never
       needs to defensively check.
     • All helpers are pure — no library / no React — so they're cheap
       to call inline in render functions.
   ────────────────────────────────────────────────────────────────────── */

type DateInput = string | Date | number | null | undefined;

const EMPTY = "—";

/** Convert any accepted input into a Date, or null when unparseable. */
function toDate(input: DateInput): Date | null {
  if (input == null || input === "") return null;
  // Strings without a time component (e.g. "2026-06-05") parse as UTC
  // midnight in some browsers, which can shift the displayed day back.
  // Append a midday local-time hint to keep date-only values stable.
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const d = new Date(input + "T12:00:00");
    return isNaN(d.getTime()) ? null : d;
  }
  const d = input instanceof Date ? input : new Date(input);
  return isNaN(d.getTime()) ? null : d;
}

/* ── 1. formatDateTime ─────────────────────────────────────────────────
   Used in detail pages, notes, bookings, assignments, follow-ups.
   Output: "May 17, 2026 · 3:32 AM"

   Why the "·" separator: a normal comma would read as "May 17, 2026,
   3:32 AM" — visually the date and time blur. The middle dot is a
   typographically clean separator widely used in enterprise CRMs.
   ────────────────────────────────────────────────────────────────────── */
export function formatDateTime(input: DateInput): string {
  const d = toDate(input);
  if (!d) return EMPTY;
  const datePart = d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  return `${datePart} · ${timePart}`;
}

/* ── 2. formatShortDate ────────────────────────────────────────────────
   Used for event dates and other date-only displays.
   Output: "Jun 5, 2027"
   ────────────────────────────────────────────────────────────────────── */
export function formatShortDate(input: DateInput): string {
  const d = toDate(input);
  if (!d) return EMPTY;
  return d.toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

/* ── 3. formatTime ─────────────────────────────────────────────────────
   Used for time-only contexts (e.g. follow-up time tile in dashboard).
   Output: "3:32 AM"
   ────────────────────────────────────────────────────────────────────── */
export function formatTime(input: DateInput): string {
  const d = toDate(input);
  if (!d) return EMPTY;
  return d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

/* ── 4. formatTooltipDate ──────────────────────────────────────────────
   Used as the underlying-detail when a relative time is shown.
   Output: "Saturday, May 17, 2026 at 3:32 AM"
   ────────────────────────────────────────────────────────────────────── */
export function formatTooltipDate(input: DateInput): string {
  const d = toDate(input);
  if (!d) return EMPTY;
  const datePart = d.toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-US", {
    hour: "numeric", minute: "2-digit", hour12: true,
  });
  return `${datePart} at ${timePart}`;
}

/* ── 5. formatRelativeTime ─────────────────────────────────────────────
   Used in tables, dashboard rows, activity feeds, timeline summaries.
   Output:
     • "just now"           (< 60 s)
     • "4 min ago"          (< 60 min)
     • "2 hours ago"        (< 24 h on the same wall-clock day)
     • "Yesterday"          (same wall-clock yesterday, regardless of hours)
     • "3 days ago"         (< 7 d)
     • "May 17"             (older — current year, drops the year)
     • "May 17, 2025"       (older — different year)
     • Future dates roll the wording to "in N min" / "in N hours" / etc.

   Wall-clock day boundaries (not 24-hour rolling) so "Yesterday" lines
   up with how operators talk — a 9 PM lead and a 6 AM follow-up will
   correctly resolve to "Yesterday" / "Today" instead of getting stuck
   on hour-counts.
   ────────────────────────────────────────────────────────────────────── */
export function formatRelativeTime(input: DateInput): string {
  const d = toDate(input);
  if (!d) return EMPTY;

  const now      = new Date();
  const diffMs   = d.getTime() - now.getTime();
  const past     = diffMs <= 0;
  const absMs    = Math.abs(diffMs);
  const absSec   = Math.floor(absMs / 1000);
  const absMin   = Math.floor(absMs / 60_000);
  const absHour  = Math.floor(absMs / 3_600_000);

  if (absSec < 60) return past ? "just now" : "in a moment";

  if (absMin < 60) {
    const unit = absMin === 1 ? "min" : "min";
    return past ? `${absMin} ${unit} ago` : `in ${absMin} ${unit}`;
  }

  // Compute wall-clock day delta (independent of time-of-day).
  const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
  const startOfThat  = new Date(d);   startOfThat.setHours(0, 0, 0, 0);
  const dayDiff      = Math.round(
    (startOfToday.getTime() - startOfThat.getTime()) / 86_400_000
  );

  if (dayDiff === 0) {
    // Same wall-clock day → keep showing hours.
    const unit = absHour === 1 ? "hour" : "hours";
    return past ? `${absHour} ${unit} ago` : `in ${absHour} ${unit}`;
  }
  if (dayDiff === 1)  return "Yesterday";
  if (dayDiff === -1) return "Tomorrow";

  if (dayDiff >= 2 && dayDiff < 7)   return `${dayDiff} days ago`;
  if (dayDiff <= -2 && dayDiff > -7) return `in ${-dayDiff} days`;

  // Beyond a week — fall back to a clean short date. If it's a
  // different calendar year, include the year.
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString("en-US", sameYear
    ? { month: "short", day: "numeric" }
    : { month: "short", day: "numeric", year: "numeric" });
}

/* ── 6. formatDateInputValue ───────────────────────────────────────────
   Safe formatter for HTML5 `<input type="date">` values — emits the
   ISO date-only ("2027-06-05") expected by the input. Different from
   the visible-display helpers above; included here so callers don't
   handcraft Date math.
   ────────────────────────────────────────────────────────────────────── */
export function formatDateInputValue(input: DateInput): string {
  const d = toDate(input);
  if (!d) return "";
  const y  = d.getFullYear();
  const m  = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}
